/**
 * Offline-aware order submission.
 *
 * Usage:
 *   const result = await submitOrderWithOfflineFallback(authAxios, payload, {
 *     source: 'giri',
 *     postActions: [{ type: 'mark_giro_stop_complete', stop_id: 'abc' }],
 *   });
 *   // result = { ok: true, online: true, order: {...} }
 *   // result = { ok: true, online: false, queued_id }
 *
 * postActions: optional array of follow-up calls to run AFTER the order is
 * successfully created (both in the online path and after sync). Supported types:
 *   - { type: 'mark_giro_stop_complete', stop_id }
 *
 * Sync:
 *   - flushPendingOrders(authAxios) is called automatically on `online` events.
 *   - Each pending order is retried in FIFO order; success → removed from queue,
 *     failure (4xx) → marked with last_error and kept for manual review.
 */
import offlineStorage from "@/utils/offlineStorage";

const isNetworkError = (err) =>
  !err.response && (err.message === "Network Error" || err.code === "ERR_NETWORK" || err.code === "ECONNABORTED");

async function runPostAction(authAxios, action, createdOrder) {
  if (!action || !action.type) return;
  if (action.type === "mark_giro_stop_complete" && action.stop_id && createdOrder?.id) {
    await authAxios.put(`/giri/fermata/${action.stop_id}/complete`, { order_id: createdOrder.id });
    return;
  }
  console.warn("[pendingOrderSync] unknown post-action", action);
}

export async function submitOrderWithOfflineFallback(authAxios, payload, { source = "cart", postActions = [] } = {}) {
  if (!navigator.onLine) {
    const entry = await offlineStorage.addPendingOrder(payload, source, postActions);
    return { ok: true, online: false, queued_id: entry.id };
  }
  try {
    const res = await authAxios.post("/orders", payload);
    // Best-effort: run post actions immediately (errors logged, not surfaced)
    for (const a of postActions || []) {
      try { await runPostAction(authAxios, a, res.data); } catch (err) {
        console.warn("[pendingOrderSync] post-action failed (online):", err?.response?.data?.detail || err?.message || err);
      }
    }
    return { ok: true, online: true, order: res.data };
  } catch (err) {
    // If it's a network error, fall back to the queue (browser thought it was online but the
    // request failed mid-flight — still save the order so it isn't lost).
    if (isNetworkError(err)) {
      const entry = await offlineStorage.addPendingOrder(payload, source, postActions);
      return { ok: true, online: false, queued_id: entry.id, fallback_reason: "network_error" };
    }
    // 4xx/5xx with response — propagate error to caller (UI can show details)
    throw err;
  }
}

let flushInFlight = false;

/** Drain the pending order queue once. Returns {sent, failed, remaining}. */
export async function flushPendingOrders(authAxios) {
  if (flushInFlight) return { sent: 0, failed: 0, remaining: null, skipped: true };
  flushInFlight = true;
  let sent = 0;
  let failed = 0;
  try {
    const queue = await offlineStorage.getPendingOrders();
    for (const entry of queue) {
      if (!navigator.onLine) break;
      try {
        const res = await authAxios.post("/orders", entry.payload);
        // Run any queued post-actions (best effort)
        for (const a of entry.post_actions || []) {
          try { await runPostAction(authAxios, a, res.data); } catch (e) {
            console.warn("[pendingOrderSync] post-action failed (sync):", e?.response?.data?.detail || e?.message || e);
          }
        }
        await offlineStorage.removePendingOrder(entry.id);
        sent += 1;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Errore sconosciuto";
        await offlineStorage.markPendingOrderAttempt(entry.id, msg);
        failed += 1;
        // If the server rejected (4xx) keep going with next entries.
        // If it's a network error, stop the loop — connection may have dropped again.
        if (isNetworkError(err)) break;
      }
    }
    const remaining = await offlineStorage.countPendingOrders();
    if (sent > 0 || failed > 0) {
      window.dispatchEvent(new CustomEvent("pendingOrdersFlushed", { detail: { sent, failed, remaining } }));
    }
    return { sent, failed, remaining };
  } finally {
    flushInFlight = false;
  }
}

/** Hook the global online event to flush automatically. Call once at app boot. */
export function installAutoSync(authAxios, { intervalMs = 60_000 } = {}) {
  const handler = () => {
    flushPendingOrders(authAxios).catch((e) => console.warn("flushPendingOrders failed", e));
  };
  window.addEventListener("online", handler);

  // Periodic retry: when on intermittent mobile signal the `online` event may
  // not fire reliably (the browser thinks the radio is up but requests still
  // fail). Every `intervalMs` we re-check: if there are pendings and we're
  // online, try to drain again. Cheap (one IndexedDB count + bail).
  const intervalId = setInterval(async () => {
    if (!navigator.onLine) return;
    try {
      const c = await offlineStorage.countPendingOrders();
      if (c > 0) handler();
    } catch (e) { console.debug("[pendingOrderSync] periodic check failed:", e?.message); }
  }, intervalMs);

  // Also try to flush at install time in case there were leftover pendings from a previous session
  if (navigator.onLine) handler();

  return () => {
    window.removeEventListener("online", handler);
    clearInterval(intervalId);
  };
}

/** Manual flush — invoked when the user taps the "in coda" badge. */
export async function manualFlush(authAxios) {
  if (!navigator.onLine) {
    return { ok: false, reason: "offline" };
  }
  const r = await flushPendingOrders(authAxios);
  return { ok: true, ...r };
}
